import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AvailableContact {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  org_id: string | null;
  org_name: string | null;
  org_classification: string | null;
}

export function useAvailableContacts(role: 'inspector' | 'client') {
  return useQuery({
    queryKey: ['available-contacts', role],
    queryFn: async () => {
      const expectedClassification = role === 'inspector' ? 'leverancier' : 'klant';
      try {
        // Step 1: Fetch organizations with the correct classification
        const { data: orgs, error: orgError } = await supabase
          .from('organizations')
          .select('id')
          .eq('classification', expectedClassification);

        if (orgError) {
          console.error('useAvailableContacts: organizations error', orgError);
          return [] as AvailableContact[];
        }

        const orgIds = orgs?.map((o: any) => o.id).filter(Boolean) || [];
        if (orgIds.length === 0) {
          return [] as AvailableContact[];
        }

        // Step 2: Fetch contacts that belong to these organizations
        const { data, error } = await supabase
          .from('contacts')
          .select(`
            id,
            email,
            first_name,
            last_name,
            org_id,
            organizations (
              id,
              name,
              classification
            )
          `)
          .in('org_id', orgIds)
          .not('email', 'is', null)
          .order('first_name', { ascending: true });

        if (error) {
          console.error('useAvailableContacts: contacts error', error);
          return [] as AvailableContact[];
        }

        // Filter out contacts that already have portal users with the same role
        const { data: existingPortalUsers, error: portalError } = await supabase
          .from('portal_users')
          .select('linked_id, role')
          .eq('role', role);

        if (portalError) {
          console.error('useAvailableContacts: portal_users error', portalError);
          return [] as AvailableContact[];
        }

        const existingLinkedIds = new Set(
          (existingPortalUsers || []).map((pu: any) => pu.linked_id).filter(Boolean)
        );

        const availableContacts = (data || [])
          .filter((contact: any) => contact?.id && !existingLinkedIds.has(contact.id))
          .map((contact: any) => {
            const org = (contact as any)?.organizations as any;
            const orgName = org && typeof org === 'object' ? (org.name ?? null) : null;
            const orgClassification = org && typeof org === 'object' ? (org.classification ?? null) : null;
            return {
              id: contact.id as string,
              email: (contact.email ?? '') as string,
              first_name: (contact.first_name ?? null) as string | null,
              last_name: (contact.last_name ?? null) as string | null,
              org_id: (contact.org_id ?? null) as string | null,
              org_name: orgName,
              org_classification: orgClassification,
            } as AvailableContact;
          });

        return availableContacts as AvailableContact[];
      } catch (e) {
        console.error('useAvailableContacts: unexpected error', e);
        return [] as AvailableContact[];
      }
    },
  });
}
